import React from 'react';
import Icon from '../../../components/AppIcon';

const PaymentStatusTimeline = ({ currentStatus, statusHistory, currentLanguage }) => {
  const translations = {
    en: {
      pending: 'Pending',
      authorized: 'Authorized',
      held: 'Held in Escrow',
      released: 'Released',
      refunded: 'Refunded',
      failed: 'Failed',
      cancelled: 'Cancelled',
      timeline: 'Payment Timeline',
      waitingForAuthorization: 'Waiting for payment authorization',
      fundsAuthorized: 'Payment authorized successfully',
      fundsHeld: 'Funds held securely in escrow',
      fundsReleased: 'Funds released to traveler',
      paymentRefunded: 'Payment refunded to buyer',
      paymentFailed: 'Payment processing failed',
      paymentCancelled: 'Payment cancelled'
    },
    ko: {
      pending: '대기 중',
      authorized: '승인됨',
      held: '에스크로 보관',
      released: '해제됨',
      refunded: '환불됨',
      failed: '실패',
      cancelled: '취소됨',
      timeline: '결제 타임라인',
      waitingForAuthorization: '결제 승인 대기 중',
      fundsAuthorized: '결제가 성공적으로 승인되었습니다',
      fundsHeld: '자금이 에스크로에 안전하게 보관되었습니다',
      fundsReleased: '자금이 여행자에게 해제되었습니다',
      paymentRefunded: '결제가 구매자에게 환불되었습니다',
      paymentFailed: '결제 처리 실패',
      paymentCancelled: '결제 취소됨'
    }
  };

  const t = translations?.[currentLanguage];

  const statusConfig = {
    pending: { icon: 'Clock', color: 'var(--color-warning)', bgColor: 'bg-warning/10' },
    authorized: { icon: 'CheckCircle', color: 'var(--color-success)', bgColor: 'bg-success/10' },
    held: { icon: 'Lock', color: 'var(--color-primary)', bgColor: 'bg-primary/10' },
    released: { icon: 'Unlock', color: 'var(--color-success)', bgColor: 'bg-success/10' },
    refunded: { icon: 'RefreshCcw', color: 'var(--color-warning)', bgColor: 'bg-warning/10' },
    failed: { icon: 'XCircle', color: 'var(--color-error)', bgColor: 'bg-error/10' },
    cancelled: { icon: 'Ban', color: 'var(--color-destructive)', bgColor: 'bg-destructive/10' }
  };

  const statuses = ['pending', 'authorized', 'held', 'released'];
  const currentIndex = statuses?.indexOf(currentStatus);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-6">
        {t?.timeline}
      </h3>
      <div className="space-y-6">
        {statuses?.map((status, index) => {
          const config = statusConfig?.[status];
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          const historyItem = statusHistory?.find(h => h?.status === status);

          return (
            <div key={status} className="relative">
              {index < statuses?.length - 1 && (
                <div className={`absolute left-5 md:left-6 top-12 md:top-14 w-0.5 h-full ${
                  isCompleted ? 'bg-primary' : 'bg-border'
                }`} />
              )}
              <div className="flex items-start gap-4 md:gap-6">
                <div className={`relative flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-base ${
                  isCompleted ? config?.bgColor : 'bg-muted'
                } ${isCurrent ? 'ring-4 ring-primary/20' : ''}`}>
                  <Icon 
                    name={config?.icon} 
                    size={20} 
                    color={isCompleted ? config?.color : 'var(--color-muted-foreground)'} 
                  />
                </div>

                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h4 className={`text-sm md:text-base font-medium ${
                      isCompleted ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {t?.[status]}
                    </h4>
                    {historyItem && (
                      <span className="text-xs md:text-sm text-muted-foreground caption whitespace-nowrap">
                        {formatDate(historyItem?.createdAt)}
                      </span>
                    )}
                  </div>

                  {historyItem?.note && (
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                      {historyItem?.note}
                    </p>
                  )}

                  {isCurrent && !historyItem && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                      <span className="text-xs md:text-sm text-primary caption">In Progress</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentStatusTimeline;